/**
 * Session debugging utilities to track auth.uid() NULL issues
 * Created: 2025-07-09 - Comprehensive session state monitoring
 */

import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface SessionDebugInfo {
  timestamp: string;
  sessionExists: boolean;
  userExists: boolean;
  userId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isExpired: boolean;
  tokenAge: number | null;
}

export const debugSession = async (): Promise<SessionDebugInfo> => {
  const { data: { session }, error } = await supabase.auth.getSession();
  
  const now = Date.now() / 1000;
  const expiresAt = session?.expires_at || null;
  const isExpired = expiresAt ? now > expiresAt : false;
  const tokenAge = expiresAt ? expiresAt - now : null;
  
  const debugInfo: SessionDebugInfo = {
    timestamp: new Date().toISOString(),
    sessionExists: !!session,
    userExists: !!session?.user,
    userId: session?.user?.id || null,
    accessToken: session?.access_token ? `${session.access_token.substring(0, 20)}...` : null,
    refreshToken: session?.refresh_token ? `${session.refresh_token.substring(0, 20)}...` : null,
    expiresAt,
    isExpired,
    tokenAge,
  };
  
  console.log('🔍 Session Debug Info:', debugInfo);
  
  if (error) {
    console.error('❌ Session Error:', error);
  }
  
  return debugInfo;
};

export const validateSessionForDatabase = async (): Promise<{
  valid: boolean;
  issues: string[];
  authUidTest?: any;
}> => {
  const issues: string[] = [];
  const debugInfo = await debugSession();
  
  if (!debugInfo.sessionExists) {
    issues.push('No session found');
  }
  
  if (!debugInfo.userExists) {
    issues.push('No user in session');
  }
  
  if (debugInfo.isExpired) {
    issues.push('Session token is expired');
  }
  
  if (debugInfo.tokenAge !== null && debugInfo.tokenAge < 300) {
    issues.push('Session token expires soon (< 5 minutes)');
  }
  
  // Test auth.uid() in database context
  let authUidTest = null;
  try {
    const { data, error } = await supabase.rpc('debug_auth_context');
    if (error) {
      issues.push(`Database auth context error: ${error.message}`);
    } else {
      authUidTest = data;
      const authUid = (data as any)?.auth_uid;
      if (!authUid) {
        issues.push('auth.uid() returns NULL in database');
      }
    }
  } catch (error: any) {
    issues.push(`Failed to test database auth context: ${error.message}`);
  }
  
  return {
    valid: issues.length === 0,
    issues,
    authUidTest
  };
};

export const refreshSessionIfNeeded = async (): Promise<{
  refreshed: boolean;
  error?: string;
}> => {
  try {
    const debugInfo = await debugSession();
    
    // Refresh if expired or expires soon
    if (debugInfo.isExpired || (debugInfo.tokenAge !== null && debugInfo.tokenAge < 300)) {
      console.log('🔄 Refreshing session...');
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Session refresh failed:', error);
        return { refreshed: false, error: error.message };
      }
      
      if (data.session) {
        console.log('✅ Session refreshed successfully');
        return { refreshed: true };
      }
    }
    
    return { refreshed: false };
  } catch (error: any) {
    console.error('❌ Error during session refresh:', error);
    return { refreshed: false, error: error.message };
  }
};